import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { borders } from '../../design-system/tokens/borders';
import { animations } from '../../design-system/tokens/animations';
import { shadows } from '../../design-system/tokens/shadows';

const fadeIn = keyframes` from { opacity: 0; } to { opacity: 1; } `;
const scaleIn = keyframes` from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } `;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: ${props => props.theme.colors.overlay};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${spacing[4]};
  animation: ${fadeIn} ${animations.duration.normal}ms ${animations.easing.easeOut};
  backdrop-filter: blur(4px);
`;

const Dialog = styled.div<{ $width?: string; $maxWidth?: string }>`
  width: ${props => props.$width || 'min(100%, 90vw)'};
  max-width: ${props => props.$maxWidth || '600px'};
  max-height: min(90vh, 900px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: ${borders.radius.xl};
  background: ${props => props.theme.colors.surface};
  box-shadow: ${shadows['2xl']};
  border: 1px solid ${props => props.theme.colors.border};
  animation: ${scaleIn} ${animations.duration.normal}ms ${animations.easing.easeOut};
`;

const Header = styled.div`
  flex-shrink: 0;
  padding: ${spacing[4]} ${spacing[5]};
  border-bottom: ${borders.width.thin} solid ${props => props.theme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${spacing[3]};
`;

const Title = styled.h2`
  margin: 0;
  color: ${props => props.theme.colors.text};
  font-size: ${typography.fontSize.xl};
  font-weight: ${typography.fontWeight.semibold};
  font-family: ${typography.fontFamily.display};
  line-height: 1.25;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: ${typography.fontSize['2xl']};
  cursor: pointer;
  color: ${props => props.theme.colors.textSecondary};
  padding: 0;
  width: 44px;
  height: 44px;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${borders.radius.md};
  flex-shrink: 0;
  transition: ${animations.transition(
    ['background-color', 'color'],
    animations.duration.fast,
    animations.easing.easeOut
  )};

  &:hover {
    background: ${props => props.theme.colors.surfaceHover};
    color: ${props => props.theme.colors.text};
  }
`;

const Body = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  padding: ${spacing[4]} ${spacing[5]};
  overflow-y: auto;
`;

const Footer = styled.div`
  flex-shrink: 0;
  padding: ${spacing[3]} ${spacing[5]};
  border-top: ${borders.width.thin} solid ${props => props.theme.colors.border};
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: ${spacing[3]};
  background: ${props => props.theme.colors.surface};
`;

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  width?: string;
  maxWidth?: string;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  width,
  maxWidth,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const titleId = 'modal-title';

  return (
    <Overlay onClick={onClose} role="presentation">
      <Dialog
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        $width={width}
        $maxWidth={maxWidth}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        <Header>
          <Title id={titleId}>{title}</Title>
          <CloseButton onClick={onClose} aria-label={t('common.close')} type="button">
            ×
          </CloseButton>
        </Header>
        <Body>{children}</Body>
        {footer && <Footer>{footer}</Footer>}
      </Dialog>
    </Overlay>
  );
};

export default Modal;
